import { Badge } from "@/components/ui/badge";

import type { GridRow, Market, PriceRunLine } from "./api";
import { MovesLines } from "./AsOfParts";
import { COPY, fulfilmentFlags, humanDate, MARKET_LABEL, MISSING_LABEL, money, orders, storeNote, vatPct } from "./format";
import { overrideChipText, overrideLineText, savedOverrideText, type Override } from "./overrides";
import { RrpCell } from "./RrpCell";
import type { RrpLookup } from "./rrp";

/** The saved chip and the committed-today line under the committed price: the batch state of the row, from C2's price-run reads.
 * Moved from the Price cell to the drawer by c1-price-run-signal-rows (CLB-022), word for word. */
export function BatchLines({ saved, committed, override }: { saved: PriceRunLine | null; committed: PriceRunLine | null; override: Override | null }) {
  const savedTemporary = saved ? savedOverrideText(saved) : null;
  return (
    <>
      {/* C1 v1.50: the override standing on the row's committed price (C2 v1.52), in force with its days left or due its return. */}
      {override && (
        <div className="mt-1">
          <Badge variant="outline" className={override.status === "due" ? "border-ks-warn text-ks-warn" : "border-ks-purple text-ks-purple"} data-override-chip>{overrideChipText(override)}</Badge>
          <div className="text-xs text-ks-grey-1">{overrideLineText(override)}</div>
        </div>
      )}
      {saved && <div className="mt-1"><Badge variant="outline" className="border-ks-warn text-ks-warn">{COPY.savedChip(money(saved.new_price, saved.currency))}</Badge></div>}
      {savedTemporary && <div className="text-xs text-ks-warn" data-saved-temporary>{savedTemporary}</div>}
      {/* A saved line names the reason it was saved with (C1 v1.35). The words do not say "below floor": a reason is also asked
          where the floor is in another currency, and the browser never judges the floor. */}
      {saved?.floor_override_reason && <div className="text-xs text-ks-warn" data-saved-override>{COPY.savedOverride(saved.floor_override_reason)}</div>}
      {committed && <div className="text-xs text-ks-grey-1">{COPY.committedLine(money(committed.new_price, committed.currency), humanDate(committed.committed_at))}</div>}
    </>
  );
}

/** What the grid row's cells said before they became pure signal (CLB-022), at the top of the drawer: the price in force with its
 * VAT and set date, the batch state, the RRP line with its fee-reason action, the fulfilment flags, the store note and, on a picked
 * date, what moved. Every sentence is the one the cell showed. */
export function DrawerRowLines({ row, market, saved, committed, override, rrp, areaName, picked }: {
  row: GridRow; market: Market; saved: PriceRunLine | null; committed: PriceRunLine | null; override: Override | null;
  rrp: RrpLookup; areaName: string; picked: boolean;
}) {
  const flags = fulfilmentFlags(row);
  return (
    <div className="mt-3 text-sm" data-drawer-row-lines>
      {row.price && (
        <div className="text-xs text-ks-grey-1">
          {money(row.price.value, row.price.currency)} · {row.price.currency} · {row.grain === "store" ? "inc VAT, rate by ship country" : `inc VAT ${vatPct(row.vat_rate)}`} · set {humanDate(row.price.set_at)}
        </div>
      )}
      {!row.price && <div className="text-xs text-ks-grey-1">{MISSING_LABEL.no_price} · none in the price log</div>}
      <BatchLines saved={saved} committed={committed} override={override} />
      {row.grain !== "ship_country" && <RrpCell lookup={rrp} market={market} saved={saved} areaName={areaName} />}
      {row.grain === "store" && row.weighting && row.weighting.countries_total > 0 && (
        <div className="text-xs text-ks-grey-1">{row.weighting.countries_total} ship {row.weighting.countries_total === 1 ? "country" : "countries"} · {orders(row.weighting.orders_total)}</div>
      )}
      {row.grain === "store" && row.weighting && <div className="text-xs text-ks-grey-1">{storeNote(row.weighting, MARKET_LABEL[market])}</div>}
      {flags.map((flag) => <div key={flag.text} className="mt-1"><Badge variant="outline" className="border-ks-warn text-ks-warn" title={flag.title}>{flag.text}</Badge></div>)}
      {picked && <MovesLines row={row} currency={row.currency ?? row.price?.currency ?? null} />}
    </div>
  );
}
